import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  List,
  Row,
  Statistic,
  Tag,
  Typography,
  message,
  theme
} from "antd";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../shared/PageHeader";
import { formatCents, formatDate, formatPeriod } from "../shared/money";
import { errorText, openBlob, request } from "../api";
import { getOverview } from "../reports/api";

const Overview = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerCredits, setCustomerCredits] = useState(null);

  useEffect(() => {
    getOverview()
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
    // Money owed to customers (Kundenguthaben): corrections, Stornos of paid invoices, overpayments
    request
      .get("/invoicing/credits", { status: "open,refund_pending" })
      .then(res => {
        const list = res.data?.credits || [];
        setCustomerCredits({
          total: list.reduce((s, c) => s + Number(c.remaining || 0), 0),
          count: list.length,
          pending: list.filter(c => c.status === "refund_pending").length
        });
      })
      .catch(() => setCustomerCredits(null));
  }, []);

  const weeks = data?.forecast?.weeks || [];
  const sparkOptions = {
    chart: { type: "area", sparkline: { enabled: true }, animations: { enabled: false } },
    colors: [data?.forecast?.alert ? token.colorError : token.colorPrimary],
    stroke: { width: 2, curve: "straight" },
    fill: { type: "solid", opacity: 0.12 },
    tooltip: {
      x: { show: true },
      y: {
        formatter: v => `${new Intl.NumberFormat("de-DE").format(v)} €`,
        title: { formatter: () => t("Balance") }
      }
    },
    xaxis: { categories: weeks.map(w => formatDate(w.start)) }
  };

  const card = (title, value, extra, link) => (
    <Card size="small" loading={loading} className="acct-kpi">
      <Statistic title={link ? <Link to={link}>{title}</Link> : title} value={value} />
      {extra ? (
        <Typography.Text type="secondary" className="acct-wrap">
          {extra}
        </Typography.Text>
      ) : null}
    </Card>
  );

  return (
    <div>
      <PageHeader
        title={t("Finance overview")}
        subtitle={data ? `${t("As of")} ${formatDate(data.today)}` : ""}
      />
      {data && !data.setupCompletedAt ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("Accounting is not set up yet.")}
          description={
            <span>
              {t("Choose your chart of accounts, tax details and tax advisor first.")}{" "}
              <Link to="/user/accounting/settings">{t("Open settings")}</Link>
            </span>
          }
        />
      ) : null}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          {card(
            t("Bank + cash"),
            formatCents(data?.bank?.total),
            data
              ? `${t("Bank")} ${formatCents(data.bank?.bank)} · ${t("Cash")} ${formatCents(data.bank?.cash)}`
              : "",
            "/user/accounting/bank"
          )}
        </Col>
        <Col xs={24} sm={12} xl={6}>
          {card(
            t("Open receivables"),
            formatCents(data?.receivables?.total),
            data
              ? `${data.receivables?.overdueCount ?? 0} ${t("overdue")}: ${formatCents(data.receivables?.overdue)}`
              : "",
            "/user/accounting/receivables"
          )}
        </Col>
        {customerCredits && customerCredits.total > 0 ? (
          <Col xs={24} sm={12} xl={6}>
            {card(
              t("Credits owed to customers"),
              formatCents(customerCredits.total),
              `${customerCredits.count} ${t("credits")}${
                customerCredits.pending
                  ? ` · ${customerCredits.pending} ${t("refunds pending")}`
                  : ""
              }`,
              "/user/accounting/receivables"
            )}
          </Col>
        ) : null}
        <Col xs={24} sm={12} xl={6}>
          {card(
            t("Open payables"),
            formatCents(data?.payables?.total),
            data ? `${t("overdue")}: ${formatCents(data.payables?.overdue)}` : "",
            "/user/accounting/belege"
          )}
        </Col>
        <Col xs={24} sm={12} xl={6}>
          {card(
            `${t("VAT estimate")} ${data ? formatPeriod(data.period) : ""}`,
            formatCents(data?.vat?.estimate),
            data?.vat?.dueDate
              ? `${t("Due")} ${formatDate(data.vat?.dueDate)}${data.vat?.nextFiling ? ` · ${t("filing")} ${formatPeriod(data.vat.nextFiling.period)}: ${formatCents(data.vat.nextFiling.amountDue)}` : ""}`
              : "",
            "/user/accounting/tax"
          )}
        </Col>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            loading={loading}
            title={
              <Link to="/user/accounting/reports/liquidity">{t("Cash forecast, 13 weeks")}</Link>
            }
          >
            {data?.forecast ? (
              <>
                {data.forecast?.alert ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message={`${t("Below floor in week")} ${data.forecast.alert.week + 1}: ${formatCents(data.forecast.alert.balance)}`}
                  />
                ) : null}
                <Chart
                  type="area"
                  width="100%"
                  height={90}
                  series={[
                    { name: t("Balance"), data: weeks.map(w => Math.round(w.balance / 100)) }
                  ]}
                  options={sparkOptions}
                />
                <Typography.Text type="secondary">
                  {weeks.length
                    ? `${t("Week")} 13: ${formatCents(weeks[weeks.length - 1].balance)}`
                    : ""}
                </Typography.Text>
              </>
            ) : (
              <Typography.Text type="secondary">{t("No forecast available")}</Typography.Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            loading={loading}
            title={<Link to="/user/accounting/deadlines">{t("Next deadlines")}</Link>}
          >
            <List
              size="small"
              dataSource={data?.deadlines || []}
              locale={{ emptyText: t("No open deadlines") }}
              renderItem={d => (
                <List.Item style={{ justifyContent: "flex-start", gap: 8, minHeight: 40 }}>
                  <Tag color={d.status === "overdue" ? "error" : "processing"} className="acct-num">
                    {formatDate(d.dueDate)}
                  </Tag>
                  <span className="acct-wrap">
                    {d.link ? <Link to={d.link}>{d.title}</Link> : d.title}
                  </span>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={12} xl={6}>
          {card(t("Receipts to review"), data?.inbox ?? 0, "", "/user/accounting/belege")}
        </Col>
        <Col xs={12} xl={6}>
          {card(
            `${t("Revenue")} ${data ? formatPeriod(data.period) : ""}`,
            formatCents(data?.month?.revenue),
            data ? `${t("Cumulative")}: ${formatCents(data.month?.revenueCumulative)}` : "",
            "/user/accounting/reports/bwa"
          )}
        </Col>
        <Col xs={12} xl={6}>
          {card(
            `${t("Result")} ${data ? formatPeriod(data.period) : ""}`,
            formatCents(data?.month?.result),
            data ? `${t("Cumulative")}: ${formatCents(data.month?.resultCumulative)}` : "",
            "/user/accounting/reports/bwa"
          )}
        </Col>
        <Col xs={12} xl={6}>
          <Card size="small" loading={loading} className="acct-kpi">
            <Statistic
              title={
                <Link to="/user/accounting/assistant/letters">{t("Last accountant letter")}</Link>
              }
              value={data?.lastLetter ? formatPeriod(data.lastLetter.period) : "-"}
            />
            {data?.lastLetter ? (
              <Button
                type="link"
                style={{ paddingInline: 0, minHeight: 40 }}
                onClick={() =>
                  openBlob(request.blob(`/assistant/letters/${data.lastLetter.id}`)).catch(e =>
                    message.error(errorText(e))
                  )
                }
              >
                {t("Open")}
              </Button>
            ) : (
              <Typography.Text type="secondary">
                {t("Generated after each month close")}
              </Typography.Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Overview;
